/**
 * useGraphPhotos.ts
 * Resolve real MS Graph profile photos (blob URLs) for a set of user emails,
 * mirroring how the Skill Matrix loads avatars. Best-effort + cached; falls back
 * to initials when a photo is missing or Graph consent isn't granted.
 */

import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import type { PublicClientApplication } from '@azure/msal-browser';
import { fetchUserPhotos } from '../services/graphService';

export function useGraphPhotos(emails: string[]): Map<string, string | null> {
  const { instance } = useMsal();
  const [photos, setPhotos] = useState<Map<string, string | null>>(new Map());
  const joined = emails.filter(Boolean).slice().sort().join(',');

  useEffect(() => {
    const list = joined ? joined.split(',') : [];
    if (list.length === 0) return;
    let alive = true;
    void fetchUserPhotos(instance as PublicClientApplication, list)
      .then((m) => {
        if (!alive) return;
        setPhotos((prev) => {
          const next = new Map(prev);
          for (const [k, v] of m) next.set(k, v);
          return next;
        });
      })
      .catch(() => { /* photos are best-effort */ });
    return () => { alive = false; };
  }, [joined, instance]);

  return photos;
}
